import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiPlus, FiPrinter, FiXCircle, FiFileText, FiX,
  FiChevronDown, FiChevronRight,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useAuth } from '../../../context/AuthContext';
import api, { getEmpresaInfo } from '../../../services/api';
import { imprimirVenta } from '../../../utils/imprimirVenta';
import ModalReenviarWhatsapp from '../shared/ModalReenviarWhatsapp';
import '../../../styles/VentasListado.css';

// ── Constantes ──────────────────────────────────────────────────────────────

const DOCUMENT_TYPES = ['TODOS', 'PROFORMA', 'FACTURA', 'BOLETA', 'NOTA DE DÉBITO', 'NOTA DE CRÉDITO'];
const PAYMENT_METHODS = ['TODOS', 'EFECTIVO', 'CRÉDITO', 'TARJETA CRED. O DEBITO', 'TRANSFERENCIA BANCARIA', 'PLIN', 'YAPE'];
const STATUS_OPTIONS   = [{ value: '', label: 'TODOS' }, { value: 'completed', label: 'COMPLETADO' }, { value: 'cancelled', label: 'ANULADO' }];
const ITEMS_OPTIONS    = [10, 25, 50];
const QUICK_FILTERS    = [
  { value: 'todo',    label: 'Todo' },
  { value: 'contado', label: 'Contado' },
  { value: 'credito', label: 'Crédito' },
];

const EMPRESA = {
  nombre:    'PIVASAPOLLOS',
  direccion: 'AV. BENAVIDES NRO 6600-6606 MZ K LOTE 1 A MZA. K LOTE. 1 URB. LOS DIAMANTES - MIRAFLORES - LIMA',
  ruc:       '20100000001',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  // Fijo a Perú (UTC-5, sin horario de verano) — nunca la zona horaria del
  // dispositivo/navegador, para que la fecha mostrada siempre coincida con
  // el mismo ajuste que ya usa el backend en sus queries.
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Lima' });
}

function fmtMoney(v) {
  const n = parseFloat(v) || 0;
  return n.toFixed(2);
}

/** Devuelve la clase CSS del badge de forma de pago */
function badgePagoClass(method) {
  if (!method) return 'vl-badge';
  const m = method.toUpperCase();
  if (m.includes('EFECTIVO'))      return 'vl-badge vl-badge-efectivo';
  if (m.includes('CRÉDITO') || m.includes('CREDITO')) return 'vl-badge vl-badge-credito';
  if (m.includes('TARJETA'))       return 'vl-badge vl-badge-tarjeta';
  if (m.includes('TRANSFERENCIA')) return 'vl-badge vl-badge-transferencia';
  if (m.includes('PLIN'))          return 'vl-badge vl-badge-plin';
  if (m.includes('YAPE'))          return 'vl-badge vl-badge-yape';
  return 'vl-badge';
}

/** Clase CSS del badge de estado de venta */
function badgeStatusClass(status) {
  if (status === 'completed') return 'vl-badge vl-badge-completed';
  if (status === 'cancelled') return 'vl-badge vl-badge-cancelled';
  return 'vl-badge vl-badge-pending';
}

/** Etiqueta legible del estado */
function statusLabel(status) {
  if (status === 'completed') return 'COMPLETADO';
  if (status === 'cancelled') return 'ANULADO';
  return 'PENDIENTE';
}

// ── Funciones de exportación ──────────────────────────────────────────────

function exportCSV(rows) {
  const headers = ['Serie', 'Secuencia', 'DNI/RUC', 'Cliente', 'Fecha Venta', 'Total', 'Forma Pago', 'Estado'];
  const lines = [headers.join(',')];
  rows.forEach(r => {
    lines.push([
      r.serie || '',
      r.sequence || '',
      r.client_document || '',
      (r.client_name || '').replace(/,/g, ' '),
      fmtDate(r.sale_date),
      fmtMoney(r.total),
      (r.payment_method || '').replace(/,/g, ' '),
      statusLabel(r.status),
    ].join(','));
  });
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `Ventas_${todayISO()}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function exportPDF(rows, resumen) {
  const win = window.open('', '_blank');
  const trs = rows.map((r, i) => {
    const bg = i % 2 === 0 ? '#fff' : '#f0f7ff';
    return `<tr style="background:${bg}">
      <td>${r.serie || ''}</td>
      <td>${r.sequence || ''}</td>
      <td>${r.client_document || ''}</td>
      <td>${r.client_name || ''}</td>
      <td>${fmtDate(r.sale_date)}</td>
      <td style="text-align:right;font-weight:700">S/ ${fmtMoney(r.total)}</td>
      <td>${r.payment_method || ''}</td>
      <td>${statusLabel(r.status)}</td>
    </tr>`;
  }).join('');

  win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
    <title>Ventas - PIVASAPOLLOS</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:11px;padding:16px;color:#1e293b}
      h2{color:#17375e;font-size:14px;margin:0 0 4px;text-transform:uppercase}
      .sub{color:#64748b;font-size:11px;margin-bottom:12px}
      table{border-collapse:collapse;width:100%}
      th{background:#c8ddf0;color:#17375e;padding:7px 9px;text-align:left;
         border:1px solid #9ebcdf;font-size:10.5px;white-space:nowrap}
      td{padding:5px 8px;border:1px solid #dde8f2;font-size:11px}
      .footer{margin-top:10px;font-size:10px;color:#94a3b8;text-align:right}
      .resumen{margin-bottom:10px;font-size:12px;color:#334155}
    </style>
    </head><body>
    <h2>PIVASAPOLLOS — Ventas</h2>
    <div class="sub">RUC: ${EMPRESA.ruc} | ${EMPRESA.direccion}</div>
    <div class="resumen">
      <strong>${resumen.count}</strong> ventas ·
      Total: <strong>S/ ${fmtMoney(resumen.total)}</strong> ·
      Anulado: <strong>S/ ${fmtMoney(resumen.anulado)}</strong> ·
      Total Real: <strong>S/ ${fmtMoney(resumen.real)}</strong>
    </div>
    <table>
      <thead><tr>
        <th>Serie</th><th>Secuencia</th><th>DNI/RUC</th><th>Cliente</th>
        <th>Fecha</th><th>Total</th><th>Forma Pago</th><th>Estado</th>
      </tr></thead>
      <tbody>${trs}</tbody>
    </table>
    <div class="footer">PIVASAPOLLOS — Generado ${new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' })}</div>
    <script>window.onload=()=>{ window.print(); }</script>
    </body></html>`);
  win.document.close();
}

// ── API calls (locales, no modifican api.js) ──────────────────────────────

function getSales(businessId, filters = {}) {
  // limit alto: la vista agrupada de admin necesita el total de ventas del
  // rango filtrado, no solo la primera página (el backend pagina a 50 x defecto).
  let url = `/api/sales?business_id=${businessId}&limit=3000`;
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null && v !== 'TODOS') {
      url += `&${k}=${encodeURIComponent(v)}`;
    }
  });
  return api.get(url);
}

function getSale(id, businessId) {
  return api.get(`/api/sales/${id}?business_id=${businessId}`);
}

function anularVenta(id, businessId) {
  return api.patch(`/api/sales/${id}/anular?business_id=${businessId}`);
}

// ── Subcomponentes ────────────────────────────────────────────────────────

function Paginacion({ page, totalPages, perPage, setPage, setPerPage }) {
  return (
    <div className="vl-pagination">
      <span className="pg-info">({page} de {totalPages})</span>
      <button onClick={() => setPage(1)} disabled={page === 1} title="Primera">|&lt;</button>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} title="Anterior">&lt;&lt;</button>
      <span className="pg-current">{page}</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} title="Siguiente">&gt;&gt;</button>
      <button onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Última">&gt;|</button>
      <select value={perPage} onChange={e => { setPerPage(+e.target.value); setPage(1); }}>
        {ITEMS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

/** Filas de la tabla de ventas — reutilizada por la vista plana y por cada grupo expandido */
function SalesRows({ rows, selectedId, onSelectRow, onPrint, onWhatsapp, onAnular, isAdmin }) {
  return rows.map((row, i) => {
    const isCancelled = row.status === 'cancelled';
    const isSelected  = selectedId === row.id;
    let rowClass = i % 2 === 0 ? 'vl-row-even' : 'vl-row-odd';
    if (isCancelled) rowClass = 'vl-row-cancelled';
    if (isSelected)  rowClass = 'vl-row-selected';

    return (
      <tr
        key={row.id}
        className={rowClass}
        onClick={() => onSelectRow(row)}
        title="Click para ver detalle"
      >
        <td>{row.serie || ''}</td>
        <td>{row.sequence || ''}</td>
        <td>{row.client_document || ''}</td>
        <td>{row.client_name || 'VARIOS'}</td>
        <td>{fmtDate(row.sale_date)}</td>
        <td className="td-num">S/ {fmtMoney(row.total)}</td>
        <td className="no-strike">
          <span className={badgePagoClass(row.payment_method)}>
            {row.payment_method || '-'}
          </span>
        </td>
        <td className="no-strike">
          <span className={badgeStatusClass(row.status)}>
            {statusLabel(row.status)}
          </span>
        </td>
        <td className="td-actions no-strike">
          <button
            className="vl-btn-ticket"
            title="Imprimir ticket"
            onClick={e => onPrint(e, row)}
            disabled={isCancelled}
          >
            <FiPrinter size={13} />
          </button>
          {' '}
          <button
            className="vl-btn-whatsapp"
            title="Enviar por WhatsApp"
            onClick={e => onWhatsapp(e, row)}
            disabled={isCancelled}
          >
            <FaWhatsapp size={13} />
          </button>
          {' '}
          {isAdmin && (
            <button
              className="vl-btn-anular"
              title="Anular venta"
              onClick={e => onAnular(e, row)}
              disabled={isCancelled}
            >
              <FiXCircle size={13} />
            </button>
          )}
        </td>
      </tr>
    );
  });
}

const SALES_TABLE_HEAD = (
  <tr className="vl-head-row">
    <th style={{ width: 60 }}>Serie</th>
    <th style={{ width: 90 }}>Secuencia</th>
    <th style={{ width: 100 }}>DNI y/o RUC</th>
    <th>Cliente</th>
    <th style={{ width: 100 }}>Fecha Venta</th>
    <th style={{ width: 90, textAlign: 'right' }}>Total</th>
    <th style={{ width: 160 }}>Forma Pago</th>
    <th style={{ width: 100 }}>Estado</th>
    <th style={{ width: 80 }}>Acciones</th>
  </tr>
);

/** Modal de reimpresión — mismos 3 formatos que Nueva Venta (A4 / 80mm / 60mm) */
function ModalTicket({ venta, empresa, onClose }) {
  if (!venta) return null;
  const items = (venta.details || venta.items || []).map(it => ({
    quantity:     it.quantity ?? it.cantidad,
    product_name: it.product_name || it.nombre_producto || it.nombre,
    unit_price:   it.unit_price ?? it.precio_unitario,
    total:        it.total ?? it.subtotal,
    unit_measure: it.unit_measure,
    qty_unidad:   it.qty_unidad,
    special_condition: it.special_condition,
    quantity_presentation:   it.quantity_presentation,
    unit_price_presentation: it.unit_price_presentation,
    presentation_name:       it.presentation_name,
  }));
  const payments = (venta.payments || []).map(p => ({
    method: p.method_name || p.short_name || p.method || venta.payment_method,
    amount: p.amount,
  }));

  const handlePrint = (fmt) => {
    imprimirVenta({
      empresa,
      serie: venta.serie,
      secuencia: venta.sequence,
      client_name: venta.client_name,
      document_number: venta.client_document,
      date: venta.sale_date || venta.created_at,
      seller: venta.seller_name || venta.username,
      items,
      // `sales` no tiene columna payment_method — las ventas al crédito no
      // generan sale_payments (se cobran después vía CxC), así que "payments"
      // llega vacío; se usa payment_status para mostrar algo correcto en vez
      // de dejar "FORMA PAGO" en blanco.
      payments: payments.length
        ? payments
        : [{ method: venta.payment_status === 'credit' ? 'CRÉDITO' : '-' }],
      subtotal: venta.subtotal,
      discount: venta.discount,
      total: venta.total,
      notes: venta.notes,
    }, fmt);
  };

  return (
    <div className="vl-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="vl-modal-ticket">
        <div className="vl-modal-header">
          <span>Reimprimir comprobante — {venta.serie}-{venta.sequence}</span>
          <button className="vl-modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className="vl-modal-body">
          <div style={{ padding: '20px 24px', fontSize: 13, color: '#334155' }}>
            <div><strong>Cliente:</strong> {venta.client_name || 'CLIENTES VARIOS'}</div>
            <div><strong>Total:</strong> S/ {fmtMoney(venta.total)}</div>
            <div style={{ marginTop: 10, color: '#64748b' }}>Elige el formato de impresión:</div>
          </div>
        </div>
        <div className="vl-modal-footer">
          <button className="vl-btn-imprimir" onClick={() => handlePrint('a4')}>
            <FiPrinter size={14} /> A4
          </button>
          <button className="vl-btn-imprimir" onClick={() => handlePrint('80')}>
            <FiPrinter size={14} /> 80mm
          </button>
          <button className="vl-btn-imprimir" onClick={() => handlePrint('60')}>
            <FiPrinter size={14} /> 60mm
          </button>
          <button className="vl-btn-cerrar" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

/** Modal de confirmación de anulación */
function ModalAnular({ venta, onConfirm, onClose, loading }) {
  return (
    <div className="vl-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="vl-modal-anular">
        <div className="vl-modal-header">
          <span>Anular Venta</span>
          <button className="vl-modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className="vl-modal-body">
          <div className="vl-anular-warning">
            Esta acción <strong>no se puede deshacer</strong>. La venta quedará marcada como anulada
            y el stock de los productos será restaurado.
          </div>
          <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.6 }}>
            <strong>Venta:</strong> {venta.serie}-{venta.sequence}<br />
            <strong>Cliente:</strong> {venta.client_name || 'VARIOS'}<br />
            <strong>Total:</strong> S/ {fmtMoney(venta.total)}<br />
            <strong>Fecha:</strong> {fmtDate(venta.sale_date)}
          </div>
        </div>
        <div className="vl-modal-footer">
          <button
            className="vl-btn-confirmar-anular"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Anulando...' : 'Confirmar Anulación'}
          </button>
          <button className="vl-btn-cerrar" onClick={onClose} disabled={loading}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────

function VentasListado({ businessId: propBusinessId }) {
  const { user } = useAuth();
  const navigate  = useNavigate();
  // role_is_admin es el rol fino (roles/permisos), no el `role` grueso del JWT —
  // mismo criterio que Dashboard/Reportes.
  const isAdmin = !!user?.role_is_admin;

  // Resuelve businessId: prop o desde el usuario autenticado
  const businessId = propBusinessId || user?.business_id || 1;

  // ── Estado de filtros ────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    from_date:      todayISO(),
    to_date:        todayISO(),
    document_type:  '',
    payment_method: '',
    status:         '',
    search:         '',
  });

  // ── Estado de datos ──────────────────────────────────────────────────────
  const [sales, setSales]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Filtro rápido Todo/Contado/Crédito — instantáneo sobre lo ya cargado,
  // sin pasar por el select "Forma de Pago" + botón Buscar.
  const [quickFilter, setQuickFilter] = useState('todo');
  const displaySales = useMemo(() => {
    if (quickFilter === 'contado') {
      return sales.filter(s => s.payment_status !== 'credit' && s.payment_status !== 'partial');
    }
    if (quickFilter === 'credito') {
      return sales.filter(s => s.payment_status === 'credit' || s.payment_status === 'partial');
    }
    return sales;
  }, [sales, quickFilter]);

  // ── Paginación ───────────────────────────────────────────────────────────
  const [page, setPage]         = useState(1);
  const [perPage, setPerPage]   = useState(10);

  // ── Selección y detalle ──────────────────────────────────────────────────
  const [selectedId, setSelectedId]       = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);

  // ── Modales ──────────────────────────────────────────────────────────────
  const [ticketVenta, setTicketVenta]     = useState(null);    // objeto venta completo para ticket
  const [waVenta, setWaVenta]             = useState(null);    // venta a enviar por WhatsApp
  const [anularVentaObj, setAnularVentaObj] = useState(null);  // objeto venta a anular
  const [anulando, setAnulando]           = useState(false);

  // ── Datos de la empresa (para el comprobante impreso) ────────────────────
  const [empresa, setEmpresa] = useState(null);
  useEffect(() => {
    getEmpresaInfo(businessId).then(r => setEmpresa(r.data?.data || null)).catch(() => {});
  }, [businessId]);

  // ── Carga de ventas ──────────────────────────────────────────────────────
  const loadSales = useCallback(async () => {
    setLoading(true);
    setSelectedId(null);
    setSelectedDetail(null);
    try {
      const res = await getSales(businessId, filters);
      setSales(res.data.rows || []);
      setPage(1);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      alert('Error al cargar ventas: ' + msg);
    } finally {
      setLoading(false);
    }
  }, [businessId, filters]);

  // Carga inicial al montar
  useEffect(() => { loadSales(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resumen calculado ────────────────────────────────────────────────────
  const resumen = useMemo(() => {
    const completed = displaySales.filter(s => s.status !== 'cancelled');
    const cancelled = displaySales.filter(s => s.status === 'cancelled');
    const total   = completed.reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
    const anulado = cancelled.reduce((acc, s) => acc + (parseFloat(s.total) || 0), 0);
    return {
      count:   completed.length,
      total,
      anulado,
      real:    total - anulado,
    };
  }, [displaySales]);

  // ── Paginación de resultados (solo vista plana, no-admin) ────────────────
  const totalPages = Math.max(1, Math.ceil(displaySales.length / perPage));
  const paged      = displaySales.slice((page - 1) * perPage, page * perPage);

  // ── Vista agrupada por usuario/caja (solo admin) ─────────────────────────
  // Desglosada (expandida) por defecto — el usuario puede colapsar grupos
  // puntuales, pero cada recarga/filtro vuelve a mostrarlos todos abiertos.
  const [expandedSellers, setExpandedSellers] = useState(new Set());
  const toggleSeller = (key) => setExpandedSellers(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const groups = useMemo(() => {
    if (!isAdmin) return [];
    const map = new Map();
    displaySales.forEach(s => {
      const key = s.seller_id || 'sin-vendedor';
      if (!map.has(key)) {
        map.set(key, {
          key,
          seller_name: s.seller_name || 'Sin vendedor',
          caja_name:   s.caja_name || '—',
          rows: [],
        });
      }
      map.get(key).rows.push(s);
    });
    return Array.from(map.values())
      .map(g => {
        const completed = g.rows.filter(r => r.status !== 'cancelled');
        const cancelled = g.rows.filter(r => r.status === 'cancelled');
        const total   = completed.reduce((acc, r) => acc + (parseFloat(r.total) || 0), 0);
        const anulado = cancelled.reduce((acc, r) => acc + (parseFloat(r.total) || 0), 0);
        return { ...g, count: completed.length, total, anulado, real: total - anulado };
      })
      .sort((a, b) => a.seller_name.localeCompare(b.seller_name));
  }, [displaySales, isAdmin]);

  useEffect(() => {
    setExpandedSellers(new Set(groups.map(g => g.key)));
  }, [groups]);

  // ── Handlers de filtros ──────────────────────────────────────────────────
  const setFilter = (key, value) =>
    setFilters(prev => ({ ...prev, [key]: value }));

  const handleBuscar = (e) => {
    e.preventDefault();
    loadSales();
  };

  // ── Seleccionar fila y cargar detalle ────────────────────────────────────
  const handleSelectRow = async (venta) => {
    if (selectedId === venta.id) {
      setSelectedId(null);
      setSelectedDetail(null);
      return;
    }
    setSelectedId(venta.id);
    setSelectedDetail(null);
    setLoadingDetail(true);
    try {
      const res = await getSale(venta.id, businessId);
      setSelectedDetail(res.data.data || res.data);
    } catch {
      // Si falla el detalle usa el objeto básico
      setSelectedDetail(venta);
    } finally {
      setLoadingDetail(false);
    }
  };

  // ── Imprimir ticket ──────────────────────────────────────────────────────
  const handlePrintTicket = async (e, venta) => {
    e.stopPropagation();
    if (venta.status === 'cancelled') return;
    try {
      const res = await getSale(venta.id, businessId);
      setTicketVenta(res.data.data || res.data);
    } catch {
      setTicketVenta(venta);
    }
  };

  // ── Enviar por WhatsApp ───────────────────────────────────────────────────
  const handleWhatsappClick = async (e, venta) => {
    e.stopPropagation();
    if (venta.status === 'cancelled') return;
    try {
      const res = await api.get('/api/whatsapp/status');
      if (res.data.data?.status !== 'connected') {
        alert('WhatsApp no está vinculado. Ve a Configuración → WhatsApp para vincularlo.');
        return;
      }
    } catch {
      alert('No se pudo verificar el estado de WhatsApp.');
      return;
    }
    setWaVenta(venta);
  };

  // ── Anular venta ─────────────────────────────────────────────────────────
  const handleAnularClick = (e, venta) => {
    e.stopPropagation();
    if (venta.status === 'cancelled') return;
    setAnularVentaObj(venta);
  };

  const confirmarAnulacion = async () => {
    if (!anularVentaObj) return;
    setAnulando(true);
    try {
      await anularVenta(anularVentaObj.id, businessId);
      setAnularVentaObj(null);
      if (selectedId === anularVentaObj.id) {
        setSelectedId(null);
        setSelectedDetail(null);
      }
      await loadSales();
    } catch (err) {
      alert('Error al anular venta: ' + (err.response?.data?.error || err.message));
    } finally {
      setAnulando(false);
    }
  };

  // ── Exportar ──────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (displaySales.length === 0) return;
    exportCSV(displaySales);
  };

  const handleExportPDF = () => {
    if (displaySales.length === 0) return;
    exportPDF(displaySales, resumen);
  };

  // ── Detalle del ítem seleccionado ────────────────────────────────────────
  const detailItems = useMemo(() => {
    if (!selectedDetail) return [];
    return selectedDetail.items || selectedDetail.details || [];
  }, [selectedDetail]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="vl-container">

      {/* Barra de filtros */}
      <form className="vl-filters" onSubmit={handleBuscar}>
        <div className="vl-filter-group">
          <label>Fecha Inicio</label>
          <input
            type="date"
            value={filters.from_date}
            onChange={e => setFilter('from_date', e.target.value)}
          />
        </div>

        <div className="vl-filter-group">
          <label>Fecha Fin</label>
          <input
            type="date"
            value={filters.to_date}
            onChange={e => setFilter('to_date', e.target.value)}
          />
        </div>

        <div className="vl-filter-group">
          <label>Tipo Documento</label>
          <select
            value={filters.document_type}
            onChange={e => setFilter('document_type', e.target.value)}
          >
            {DOCUMENT_TYPES.map(dt => (
              <option key={dt} value={dt === 'TODOS' ? '' : dt}>{dt}</option>
            ))}
          </select>
        </div>

        <div className="vl-filter-group">
          <label>Forma de Pago</label>
          <select
            value={filters.payment_method}
            onChange={e => setFilter('payment_method', e.target.value)}
          >
            {PAYMENT_METHODS.map(pm => (
              <option key={pm} value={pm === 'TODOS' ? '' : pm}>{pm}</option>
            ))}
          </select>
        </div>

        <div className="vl-filter-group">
          <label>Estado</label>
          <select
            value={filters.status}
            onChange={e => setFilter('status', e.target.value)}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="vl-filter-group search">
          <label>Busqueda</label>
          <input
            type="text"
            placeholder="Cliente, DNI/RUC, serie..."
            value={filters.search}
            onChange={e => setFilter('search', e.target.value)}
          />
        </div>

        <button type="submit" className="vl-btn-buscar" disabled={loading}>
          <FiSearch size={13} />
          {loading ? 'Buscando...' : 'Buscar'}
        </button>
      </form>

      {/* Barra de resumen y acciones */}
      <div className="vl-summary-bar">
        <div className="vl-summary-text">
          <strong>{resumen.count}</strong> venta{resumen.count !== 1 ? 's' : ''} por un
          Total: <strong>S/ {fmtMoney(resumen.total)}</strong>
          {' · '}Anulado: <span className="vl-anulado">S/ {fmtMoney(resumen.anulado)}</span>
          {' · '}Total Real: <span className="vl-total-real">S/ {fmtMoney(resumen.real)}</span>
        </div>
        <div className="vl-quick-filters">
          {QUICK_FILTERS.map(qf => (
            <button
              key={qf.value}
              type="button"
              className={`vl-btn-quick vl-btn-quick-${qf.value}${quickFilter === qf.value ? ' active' : ''}`}
              onClick={() => setQuickFilter(qf.value)}
            >
              {qf.label}
            </button>
          ))}
        </div>
        <div className="vl-summary-actions">
          <button className="vl-btn-nueva" onClick={() => navigate('/ventas/nueva')}>
            <FiPlus size={13} /> Nueva Venta
          </button>
          <button className="vl-btn-excel" onClick={handleExportCSV} title="Exportar CSV">
            <span style={{ fontSize: 13 }}>&#128038;</span> Excel
          </button>
          <button className="vl-btn-pdf" onClick={handleExportPDF} title="Exportar PDF">
            <FiFileText size={12} /> PDF
          </button>
        </div>
      </div>

      {isAdmin ? (
        /* Vista admin: agrupado por Usuario/Caja, desglosable */
        <div className="vl-table-wrap">
          {loading ? (
            <div className="vl-loading">Cargando ventas...</div>
          ) : groups.length === 0 ? (
            <div className="vl-loading">Sin registros para los filtros seleccionados</div>
          ) : (
            <table className="vl-table vl-table-grouped">
              <thead>
                <tr className="vl-head-row">
                  <th style={{ width: 30 }}></th>
                  <th>Usuario</th>
                  <th style={{ width: 160 }}>Caja</th>
                  <th style={{ width: 90, textAlign: 'right' }}>N&deg; Ventas</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Total</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Anulado</th>
                  <th style={{ width: 110, textAlign: 'right' }}>Total Real</th>
                </tr>
              </thead>
              <tbody>
                {groups.map(g => {
                  const isOpen = expandedSellers.has(g.key);
                  return (
                    <React.Fragment key={g.key}>
                      <tr
                        className="vl-group-row"
                        onClick={() => toggleSeller(g.key)}
                        title="Click para desglosar"
                      >
                        <td>{isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}</td>
                        <td>{g.seller_name}</td>
                        <td>{g.caja_name}</td>
                        <td className="td-num">{g.count}</td>
                        <td className="td-num">S/ {fmtMoney(g.total)}</td>
                        <td className="td-num">S/ {fmtMoney(g.anulado)}</td>
                        <td className="td-num" style={{ fontWeight: 700 }}>S/ {fmtMoney(g.real)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="vl-group-detail">
                          <td colSpan={7} style={{ padding: 0 }}>
                            <table className="vl-table vl-table-nested">
                              <thead>{SALES_TABLE_HEAD}</thead>
                              <tbody>
                                <SalesRows
                                  rows={g.rows}
                                  selectedId={selectedId}
                                  onSelectRow={handleSelectRow}
                                  onPrint={handlePrintTicket}
                                  onWhatsapp={handleWhatsappClick}
                                  onAnular={handleAnularClick}
                                  isAdmin={isAdmin}
                                />
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <>
          {/* Barra de paginación superior */}
          <div className="vl-export-bar">
            <Paginacion
              page={page}
              totalPages={totalPages}
              perPage={perPage}
              setPage={setPage}
              setPerPage={setPerPage}
            />
          </div>

          {/* Tabla */}
          <div className="vl-table-wrap">
            {loading ? (
              <div className="vl-loading">Cargando ventas...</div>
            ) : (
              <table className="vl-table">
                <thead>{SALES_TABLE_HEAD}</thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr className="mt-empty">
                      <td colSpan={9}>Sin registros para los filtros seleccionados</td>
                    </tr>
                  ) : (
                    <SalesRows
                      rows={paged}
                      selectedId={selectedId}
                      onSelectRow={handleSelectRow}
                      onPrint={handlePrintTicket}
                      onWhatsapp={handleWhatsappClick}
                      onAnular={handleAnularClick}
                      isAdmin={isAdmin}
                    />
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Paginación inferior */}
          <div className="vl-export-bar" style={{ borderTop: '1px solid #e2e8f0', borderBottom: 'none' }}>
            <Paginacion
              page={page}
              totalPages={totalPages}
              perPage={perPage}
              setPage={setPage}
              setPerPage={setPerPage}
            />
          </div>
        </>
      )}

      {/* Panel de detalle inferior */}
      <div className="vl-detail-panel">
        <div className="vl-detail-header">
          <span>DETALLE DE VENTA SELECCIONADA</span>
          {selectedDetail && (
            <span className="vl-detail-client">
              {selectedDetail.client_name || 'VARIOS'}
              {' — '}
              {selectedDetail.serie}-{selectedDetail.sequence}
              {' — S/ '}
              {fmtMoney(selectedDetail.total)}
            </span>
          )}
        </div>

        {selectedDetail?.notes && (
          <div className="vl-detail-notes">
            <strong>Notas:</strong> {selectedDetail.notes}
          </div>
        )}

        {loadingDetail ? (
          <div className="vl-detail-empty">Cargando detalle...</div>
        ) : !selectedId ? (
          <div className="vl-detail-empty">Seleccione una fila para ver el detalle</div>
        ) : detailItems.length === 0 ? (
          <div className="vl-detail-empty">Sin items disponibles para esta venta</div>
        ) : (
          <div className="vl-detail-wrap">
            <table className="vl-detail-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>Item</th>
                  <th style={{ width: 90 }}>Código</th>
                  <th>Producto</th>
                  <th style={{ width: 80, textAlign: 'right' }}>Cantidad</th>
                  <th style={{ width: 60 }}>U.M</th>
                  <th style={{ width: 100, textAlign: 'right' }}>Precio Unit.</th>
                  <th style={{ width: 80, textAlign: 'right' }}>Dscto(S/)</th>
                  <th style={{ width: 70, textAlign: 'right' }}>IGV</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {detailItems.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                    <td>{item.barcode || item.codigo_barras || '-'}</td>
                    <td>{item.product_name || item.nombre_producto || item.nombre || '-'}</td>
                    <td className="td-r">
                      {parseFloat(item.quantity_presentation ?? item.quantity ?? item.cantidad ?? 0).toFixed(2)}
                    </td>
                    <td>{item.presentation_name || item.unit_measure || item.unidad_medida || item.um || 'UND'}</td>
                    <td className="td-r">
                      {fmtMoney(item.unit_price_presentation ?? item.unit_price ?? item.precio_unitario ?? 0)}
                    </td>
                    <td className="td-r">
                      {fmtMoney(item.discount || item.descuento || 0)}
                    </td>
                    <td className="td-r">0.00</td>
                    <td className="td-r" style={{ fontWeight: 700 }}>
                      {fmtMoney(item.total || item.subtotal || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Ticket */}
      {ticketVenta && (
        <ModalTicket
          venta={ticketVenta}
          empresa={empresa}
          onClose={() => setTicketVenta(null)}
        />
      )}

      {/* Modal Enviar por WhatsApp */}
      {waVenta && (
        <ModalReenviarWhatsapp
          title={`Enviar por WhatsApp — ${waVenta.serie}-${waVenta.sequence}`}
          sendEndpoint="/api/whatsapp/send-ticket"
          sendBody={{ saleId: waVenta.id }}
          onClose={() => setWaVenta(null)}
        />
      )}

      {/* Modal Anulación */}
      {anularVentaObj && (
        <ModalAnular
          venta={anularVentaObj}
          onConfirm={confirmarAnulacion}
          onClose={() => setAnularVentaObj(null)}
          loading={anulando}
        />
      )}

    </div>
  );
}

export default VentasListado;
